import { useEffect, useState, useRef } from "react";
import { Plus, Loader2, Trash2, Edit, Save, AlertCircle } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const emptyForm = { question: "", answer: "", order: 0 };

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const formRef = useRef(null);
  const questionInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await API.get("/admin/faqs");
      if (!data?.success) throw new Error(data?.message || "Failed to load FAQs");
      setFaqs(data.faqs || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === faqs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(faqs.map((f) => f._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "faqs",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        fetchFaqs();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError("");
      if (editingId) {
        await API.put(`/admin/faqs/${editingId}`, form);
      } else {
        await API.post("/admin/faqs", form);
      }
      resetForm();
      fetchFaqs();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (faq) => {
    setEditingId(faq._id);
    setForm({ question: faq.question, answer: faq.answer, order: faq.order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      questionInputRef.current?.focus({ preventScroll: true });
    }, 150);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      setSaving(true);
      await API.delete(`/admin/faqs/${id}`);
      setFaqs((prev) => prev.filter((f) => f._id !== id));
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">FAQs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Create, edit, and remove FAQ entries</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={`bg-white dark:bg-slate-800 rounded-xl border p-4 space-y-3 transition-all duration-300 scroll-mt-24 ${
          editingId
            ? "border-pink-500 ring-2 ring-pink-500/20 shadow-pink-500/10"
            : "border-gray-200 dark:border-slate-700"
        }`}
      >
        {editingId && (
          <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-slate-700/60">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
              <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                Editing FAQ: {form.question || "Untitled"}
              </span>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
            >
              Cancel Edit
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
            Question
            <input
              ref={questionInputRef}
              required
              type="text"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </label>
          <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
            Order
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </label>
        </div>
        <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200 block">
          Answer
          <textarea
            required
            rows={4}
            value={form.answer}
            onChange={(e) => setForm({ ...form, answer: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editingId ? "Update FAQ" : "Add FAQ"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700/50">
              <tr className="text-xs text-gray-500 uppercase">
                <th className="px-4 py-3 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={faqs.length > 0 && selectedIds.length === faqs.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left">Question</th>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : faqs.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-gray-500">No FAQs yet</td></tr>
              ) : (
                faqs.map((faq) => {
                  const isSelected = selectedIds.includes(faq._id);
                  return (
                  <tr key={faq._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelectRow(faq._id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100">{faq.question}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{faq.order ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{faq.updatedAt ? new Date(faq.updatedAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => handleEdit(faq)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                        <Edit className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => handleDelete(faq._id)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={faqs.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="FAQs"
      />
    </div>
  );
}
